import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowRightBold } from "react-icons/pi";
import logger from "@/utils/logger";

export default function HokCategories({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const primaryColor = content.colorPalette?.primary || "#6511D9"; // Purple
  const secondaryColor = content.colorPalette?.secondary || "#FF51BF"; // Pink
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const categories = content.categories || [
    { name: "WordPress", count: "15,296", image: "/assets/img/category/category_2_1.png" },
    { name: "Magento", count: "15,296", image: "/assets/img/category/category_2_2.png" },
    { name: "Shopify", count: "15,296", image: "/assets/img/category/category_2_3.png" },
    { name: "Site Templates", count: "15,296", image: "/assets/img/category/category_2_4.png" },
    { name: "WordPress", count: "15,296", image: "/assets/img/category/category_2_1.png" },
    { name: "React Native", count: "15,296", image: "/assets/img/category/category_2_6.png" },
    { name: "Figma Design", count: "15,296", image: "/assets/img/category/category_2_7.png" },
    { name: "WordPress", count: "15,296", image: "/assets/img/category/category_2_1.png" },
  ];

  const backgroundImage = content.backgroundImage || "/assets/img/bg/category_bg_1.png";

  return (
    <div
      className={`relative w-full pt-20 pb-10 lg:pt-24 lg:pb-10 text-white mb-16 lg:mb-32 ${
        isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : ""
      }`}
      style={{
        fontFamily,
        backgroundColor: primaryColor,
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Background Soft Glows Wrapper to prevent horizontal overflow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[350px] h-[350px] rounded-full bg-purple-400/20 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        {/* Left Side: Title Area */}
        <div className="lg:col-span-4 flex flex-col text-center lg:text-left pt-4 lg:pt-12">
          <span className="inline-block self-center lg:self-start text-xs font-bold tracking-wider text-[#D4FF12] uppercase mb-4">
            <EditableText
              value={content.subtitle || "Browse Courses"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, subtitle: val })
              }
              as="span"
              placeholder="Subtitle"
            />
          </span>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-4 tracking-tight">
            <EditableText
              value={content.title || "Browse Popular Courses"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, title: val })
              }
              as="span"
              placeholder="Title"
            />
          </h2>

          <p className="text-sm sm:text-base text-gray-200/90 leading-relaxed mb-8 max-w-sm mx-auto lg:mx-0">
            <EditableText
              value={content.text || "Explore over 8000+ online courses and tutorials."}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, text: val })
              }
              as="span"
              placeholder="Description"
            />
          </p>

          <a
            href={content.ctaUrl || "#"}
            onClick={(e) => {
              if (onContentChange) {
                e.preventDefault();
              }
            }}
            className="self-center lg:self-start inline-flex items-center gap-2 border border-white/40 text-white bg-transparent hover:bg-white hover:text-gray-900 transition-all font-bold px-6 py-3 rounded-lg text-sm shadow hover:scale-[1.02]"
          >
            <EditableText
              value={content.ctaText || "View All Courses"}
              onChange={(val) =>
                onContentChange && onContentChange({ ...content, ctaText: val })
              }
              as="span"
              placeholder="CTA Text"
            />
            <PiArrowRightBold className="w-4 h-4" />
          </a>
        </div>

        {/* Right Side: Categories Grid */}
        <div className="lg:col-span-8 w-full translate-y-16 lg:translate-y-28 z-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            {categories.map((cat: any, idx: number) => (
              <div
                key={idx}
                className="group relative flex flex-col items-center justify-center pt-10 pb-8 px-6 bg-white rounded-2xl transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md hover:-translate-y-1 text-center min-h-[220px]"
              >
                <div className="relative w-12 h-12 flex items-center justify-center mb-4 transition-transform group-hover:scale-105">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-contain pointer-events-none"
                  />
                  {isSelected && (
                    <label className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 rounded-full hover:bg-black/80 cursor-pointer transition-all opacity-0 group-hover:opacity-100 font-bold text-xs">
                      Edit
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const toast = (await import("react-hot-toast")).default;
                            const homepageApi = (await import("@/services/homepageApi")).default;
                            const toastId = toast.loading("Uploading icon...");
                            try {
                              const response = await homepageApi.uploadImage(file);
                              const url = response.data?.url || response.url;
                              if (url) {
                                const newCategories = [...categories];
                                newCategories[idx] = { ...newCategories[idx], image: url };
                                onContentChange?.({ ...content, categories: newCategories });
                                toast.success("Icon updated!", { id: toastId });
                              } else {
                                toast.error("Failed to upload.", { id: toastId });
                              }
                            } catch (err) {
                              toast.error("Upload failed", { id: toastId });
                              logger.error(err);
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                <h3 className="text-gray-900 font-bold text-sm sm:text-base leading-snug">
                  <EditableText
                    value={cat.name}
                    onChange={(val) => {
                      if (!onContentChange) return;
                      const newCategories = [...categories];
                      newCategories[idx] = { ...newCategories[idx], name: val };
                      onContentChange({ ...content, categories: newCategories });
                    }}
                    as="span"
                    placeholder="Category Name"
                  />
                </h3>

                <span className="text-gray-500 text-xs sm:text-sm mt-1 sm:mt-1.5">
                  <EditableText
                    value={cat.count || "0"}
                    onChange={(val) => {
                      if (!onContentChange) return;
                      const newCategories = [...categories];
                      newCategories[idx] = { ...newCategories[idx], count: val };
                      onContentChange({ ...content, categories: newCategories });
                    }}
                    as="span"
                    placeholder="Count"
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
